import AuthService from '@core/services/AuthService';
import {useAdminStore} from '@core/store/AdminStore';
import {useMenuStore} from '@core/store/MenuStore';
import {useUserStore} from '@core/store/UserStore';
import {storeToRefs} from 'pinia';
import ModulesService from '@core/services/ModulesService';
import {RouteLocationNormalized, RouteLocationRaw} from 'vue-router';
import {useEnvironmentStore} from '@internalDevportal/store/EnvironmentStore';
import {getAppVersion} from '../utils/appVersion';

export type GuardResult = RouteLocationRaw | false | undefined;

/** Strapi page search is public when the external developer portal module is on. */
export function isPublicRoute(to: RouteLocationNormalized): boolean {
  if (to.meta.public) {
    return true;
  }
  return (
    to.name === 'search_documentation' &&
    ModulesService.externalDeveloperPortalEnabled
  );
}

/**
 * Guard class to handle route guards.
 */
export class Guard {
  private environmentsValidated = false;

  // Lazily access stores to avoid using them before Pinia is installed.
  private get adminStore() {
    return useAdminStore();
  }
  private get menuStore() {
    return useMenuStore();
  }
  private get environmentStore() {
    return useEnvironmentStore();
  }
  private get userStore() {
    return useUserStore();
  }
  private get currentMenu() {
    return storeToRefs(this.menuStore).currentMenu;
  }

  async checkVersion() {
    const appVersionAtLastLogin = localStorage.getItem('appVersionAtLastLogin');

    if (
      appVersionAtLastLogin !== getAppVersion() ||
      appVersionAtLastLogin == null
    ) {
      await AuthService.logout();
    }
  }

  /**
   * Handle correct dashboard routes.
   * @param to - The target route.
   * @returns A redirect target if the route should be rerouted, otherwise undefined.
   */
  handleDashboardRoutes(to: RouteLocationNormalized): GuardResult {
    if (
      !ModulesService.internalDeveloperPortalEnabled &&
      (to.path === '/' || to.path === '/dashboard')
    ) {
      if (ModulesService.portfolioEnabled) {
        this.menuStore.setMenu(1);
        return {name: 'portfolioDashboard'};
      } else if (ModulesService.dataDictionaryEnabled) {
        this.menuStore.setMenu(2);
        return {name: 'DataDictionaryDashboard'};
      }
    }
    return undefined;
  }

  /**
   * Handle authentication for protected routes.
   * @param to - The target route.
   * @returns `false` to cancel navigation when an external sign-in redirect was
   * triggered, otherwise undefined to continue.
   */
  async handleAuthentication(
    to: RouteLocationNormalized,
  ): Promise<false | undefined> {
    if (isPublicRoute(to)) {
      return undefined;
    }
    const user = await AuthService.ensureValidUser();
    if (!user?.access_token) {
      return await AuthService.redirectIfUnauthenticated(to);
    }
    await this.userStore.login(user);
    return undefined;
  }

  /**
   * Handle module loading and menu setting.
   * @param to - The target route.
   */
  async handleModules(to: RouteLocationNormalized): Promise<void> {
    await ModulesService.getModules();
    if (to.meta.core) {
      this.menuStore.setMenu(this.currentMenu.value);
    } else {
      const raw = to.meta.currentMenu;
      const menuIndex =
        typeof raw === 'number' && Number.isFinite(raw) ? raw : 0;
      this.menuStore.setMenu(menuIndex);
    }
  }

  /**
   * Handle not found routes based on roles and module availability.
   * @param to - The target route.
   * @returns A redirect to `notFound` if the route should not be reachable,
   * otherwise undefined.
   */
  async handleNotFound(to: RouteLocationNormalized): Promise<GuardResult> {
    if (
      to.name === 'noEnvironmentAccess' ||
      this.shouldRedirectToNoEnvironmentAccess(to)
    ) {
      return undefined;
    }
    const roles =
      this.adminStore.roles.length > 0 || isPublicRoute(to)
        ? this.adminStore.roles
        : await this.adminStore.onlyGetRoles(
            this.environmentStore.currentEnvironment.id,
          );
    const notFound = this.checkNotFoundConditions(to, roles);
    if (notFound) {
      return {name: 'notFound'};
    }
    return undefined;
  }

  /**
   * Redirect to onboarding if no environments are setup.
   * @param to - The target route.
   * @returns A redirect target if rerouting is required, otherwise undefined.
   */
  async redirectToOnboarding(
    to: RouteLocationNormalized,
  ): Promise<GuardResult> {
    if (
      (ModulesService.externalDeveloperPortalEnabled &&
        !ModulesService.internalDeveloperPortalEnabled) ||
      isPublicRoute(to)
    ) {
      return undefined;
    }
    const user = await AuthService.ensureValidUser();
    if (!user?.access_token) {
      return undefined;
    }
    if (to.name === 'noEnvironmentAccess') {
      const accessResult =
        await this.environmentStore.recheckEnvironmentAccess();
      if (accessResult === 'allowed') {
        return {name: 'dashboard'};
      }
      return undefined;
    }
    if (!this.environmentsValidated) {
      // Login already loads environments (and with a single environment that
      // pulls the whole catalog), so re-fetching here added another full
      // catalog load to the post-login wait. environmentsChecked is per session
      // and not persisted, so a hard refresh still loads once.
      if (this.environmentStore.environmentsChecked) {
        this.environmentsValidated = true;
      } else {
        const loaded = await this.environmentStore.getEnvironments();
        this.environmentsValidated = loaded;
      }
    }
    if (this.shouldRedirectToNoEnvironmentAccess(to)) {
      return {name: 'noEnvironmentAccess'};
    }
    if (
      this.environmentStore.hasNoEnvironmentsSetup &&
      (to.meta.apidex === true ||
        to.meta.core === true ||
        to.meta.admin === true) &&
      to.meta.onboarding !== true
    ) {
      return {name: 'onboarding'};
    } else if (
      to.fullPath === '/onboarding' &&
      this.environmentStore.environments.length > 0
    ) {
      return {name: 'notFound'};
    }
    return undefined;
  }

  invalidateEnvironmentAccess() {
    this.environmentsValidated = false;
  }

  shouldRedirectToNoEnvironmentAccess(to: RouteLocationNormalized): boolean {
    if (to.name === 'noEnvironmentAccess') {
      return false;
    }
    if (isPublicRoute(to)) {
      return false;
    }
    if (
      ModulesService.externalDeveloperPortalEnabled &&
      !ModulesService.internalDeveloperPortalEnabled
    ) {
      return false;
    }
    return this.hasNoEnvironmentAccess();
  }

  private hasNoEnvironmentAccess(): boolean {
    const store = this.environmentStore;
    if (store.environmentsAccessCheckFailed) {
      return false;
    }
    // Never treat the empty initial / in-flight state as "no access"; only
    // decide once a determination has completed. This avoids briefly routing
    // to noEnvironmentAccess on refresh before environments have loaded.
    if (!store.environmentsChecked || store.loadingEnvironments) {
      return false;
    }
    return (
      store.noAccessibleEnvironments ||
      (store.environments.length === 0 && !store.hasNoEnvironmentsSetup)
    );
  }

  /**
   * Check conditions for not found routes.
   * @param to - The target route.
   * @param roles - The user roles.
   * @returns {boolean} - Returns true if the route should be not found, otherwise false.
   */
  private checkNotFoundConditions(
    to: RouteLocationNormalized,
    roles: string[],
  ): boolean {
    if (isPublicRoute(to)) {
      return false;
    }
    if (to.meta.admin && !roles.includes('admin')) {
      return true;
    }
    if (to.meta.portfolio && !ModulesService.portfolioEnabled) {
      return true;
    }
    if (to.meta.apidex && !ModulesService.internalDeveloperPortalEnabled) {
      return true;
    }
    if (
      to.meta.subscriptionManager &&
      !ModulesService.subscriptionManagerEnabled
    ) {
      return true;
    }
    if (to.meta.dataDictionary && !ModulesService.dataDictionaryEnabled) {
      return true;
    }
    if (
      to.path === '/analytics' &&
      roles.includes('subscriptionManager') &&
      !roles.includes('admin')
    ) {
      return true;
    }
    return false;
  }
}
